import React, { useState } from "react";
import axios from "axios";
import { Loader2, AudioLines } from "lucide-react";
import { TOK, SERIF, SANS } from "../../pages/Soulumo/SignatureBits";

const API = process.env.REACT_APP_BACKEND_URL;
const PRESETS = [
  "I trust my hands around the green.",
  "I am calm, clear and completely present.",
  "Nothing is missing. I have everything I need right now.",
];

const Player = ({ label, src, testId }) => (
  <div className="rounded-2xl p-4 flex-1 min-w-[220px]" style={{ border: `1px solid ${TOK.goldMist}`, background: "rgba(245,236,217,0.03)" }}>
    <div className="text-[0.58rem] uppercase tracking-[0.26em] mb-2" style={{ color: TOK.goldSoft, fontFamily: SANS }}>{label}</div>
    <audio src={src} controls className="w-full" data-testid={testId} />
  </div>
);

// Before & after: the same line in Lily and in the user's clone.
export const CloneCompare = () => {
  const [text, setText] = useState(PRESETS[0]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true); setErr(""); setRes(null);
    try {
      const r = await axios.post(`${API}/api/voice-clone/compare`, { text },
        { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
      setRes(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail || "";
      setErr(d.startsWith("allowance_reached") ? "Your voice allowance for this month is used up — it resets on the 1st." : "We couldn't create the comparison. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-12" data-testid="clone-compare">
      <div className="text-[0.6rem] uppercase tracking-[0.32em] mb-3" style={{ color: TOK.goldSoft, fontFamily: SANS }}>Before &amp; after</div>
      <p className="italic mb-4" style={{ color: TOK.creamDim, fontFamily: SERIF }}>Hear one line in the Soulumo narrator and in your own voice.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map((p, i) => (
          <button key={p} type="button" onClick={() => setText(p)} data-testid={`clone-compare-preset-${i}`}
            className="rounded-full px-3 py-1.5 text-xs transition-[border-color] duration-200"
            style={{ border: `1px solid ${text === p ? TOK.gold : TOK.goldMist}`, color: TOK.creamDim }}>{p}</button>
        ))}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 300))} rows={2} data-testid="clone-compare-input"
        className="w-full rounded-2xl px-4 py-3 bg-transparent focus:outline-none text-[1.05rem]"
        style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.cream, fontFamily: SERIF }} />
      <button type="button" onClick={run} disabled={busy || text.trim().length < 3} data-testid="clone-compare-btn"
        className="mt-3 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[0.68rem] uppercase tracking-[0.22em] disabled:opacity-40"
        style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS, minHeight: 48 }}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <AudioLines size={14} />} Hear both
      </button>
      {err && <p className="mt-3 text-sm" style={{ color: "#dc8b6c" }} data-testid="clone-compare-error">{err}</p>}
      {res && (
        <div className="mt-5 flex flex-wrap gap-3" data-testid="clone-compare-results">
          <Player label="Soulumo narrator · Lily" src={res.narrator.startsWith("data:") ? res.narrator : `${API}${res.narrator}`} testId="clone-compare-narrator-audio" />
          <Player label="Your voice · clone" src={res.clone.startsWith("data:") ? res.clone : `${API}${res.clone}`} testId="clone-compare-clone-audio" />
        </div>
      )}
    </section>
  );
};
